import type { ReactNode } from 'react'
import React, { createContext, useCallback, useContext, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

interface SideNavPreference {
  isExpanded: boolean
  timestamp: number
}

interface NavigationContextValue {
  isExpanded: boolean
  isMobileMenuOpen: boolean
  activeRoute: string
  toggleExpanded: () => void
  toggleMobileMenu: () => void
  closeMobileMenu: () => void
  navigateTo: (path: string) => void
}

export type { NavigationContextValue }

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined)

// Save preference to localStorage
const saveNavPreference = (isExpanded: boolean): void => {
  try {
    const preference: SideNavPreference = {
      isExpanded,
      timestamp: Date.now()
    }
    localStorage.setItem('sideNavPreference', JSON.stringify(preference))

    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event('navigationStateChange'))
  } catch (error) {
    console.warn('Failed to save navigation preference:', error)
    // Continue without saving - non-critical feature
  }
}

// Load preference from localStorage
const loadNavPreference = (): boolean => {
  try {
    const stored = localStorage.getItem('sideNavPreference')
    if (stored) {
      const preference: SideNavPreference = JSON.parse(stored)
      return preference.isExpanded
    }
  } catch (error) {
    console.warn('Failed to load navigation preference:', error)
  }
  // Fallback to default based on viewport
  return typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
}

interface NavigationProviderProps {
  children: ReactNode
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ children }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [isExpanded, setIsExpanded] = useState(() => loadNavPreference())
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => {
      const newValue = !prev
      saveNavPreference(newValue)
      return newValue
    })
  }, [])

  const toggleMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(prev => !prev)
  }, [])

  const closeMobileMenu = useCallback(() => {
    setIsMobileMenuOpen(false)
  }, [])

  const navigateTo = useCallback((path: string) => {
    navigate(path)
    setIsMobileMenuOpen(false)
  }, [navigate])

  const value: NavigationContextValue = {
    isExpanded,
    isMobileMenuOpen,
    activeRoute: location.pathname,
    toggleExpanded,
    toggleMobileMenu,
    closeMobileMenu,
    navigateTo
  }

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  )
}

export const useNavigation = (): NavigationContextValue => {
  const context = useContext(NavigationContext)
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider')
  }
  return context
}
